import Link from "next/link";
import styles from "../../styles/FashionLibrary.module.css";
import { shortTitle, niceDate } from "../lib/lookFormat";

// Placeholder cover until a look has real art: its own palette, a gown shaped by the look's type
// (ball gown for events, mini dress for trends, gown + heart for love looks, gown + star for fantasy).
const SKIRTS = {
  Event: "M108 196 C 84 250 62 300 46 352 Q 150 372 254 352 C 238 300 216 250 192 196 Z",
  Love: "M110 196 C 90 250 70 300 58 352 Q 150 368 242 352 C 230 300 210 250 190 196 Z",
  Fantasy: "M108 196 C 80 250 52 300 34 352 Q 150 376 266 352 C 248 300 220 250 192 196 Z",
  Trend: "M112 196 C 104 222 96 246 88 268 Q 150 282 212 268 C 204 246 196 222 188 196 Z",
};

export function LookArt({ look, big }) {
  const [a, b, c, d] = look.pal;
  const id = `g-${look.slug}${big ? "-b" : ""}`;
  return (
    <svg className={styles.art} viewBox="0 0 300 400" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0.4" y2="1">
          <stop offset="0" stopColor={b} />
          <stop offset="1" stopColor={a} />
        </linearGradient>
        <linearGradient id={`${id}-d`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={c} />
          <stop offset="1" stopColor={d} />
        </linearGradient>
      </defs>
      <rect width="300" height="400" fill={`url(#${id})`} />
      <circle cx="236" cy="78" r="58" fill={d} opacity="0.28" />
      <circle cx="58" cy="300" r="90" fill={c} opacity="0.14" />
      {/* hanger */}
      <path d="M150 54 q 0 -18 14 -18 q 12 0 12 12 q 0 10 -12 14 L150 70 L94 104 Q 88 108 94 110 L206 110 Q 212 108 206 104 L150 70"
        fill="none" stroke="#ffffff" strokeOpacity="0.85" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" />
      {/* bodice + skirt */}
      <path d="M124 112 L176 112 L186 150 Q 192 176 192 196 L108 196 Q 108 176 114 150 Z" fill={`url(#${id}-d)`} />
      <path d={SKIRTS[look.type] || SKIRTS.Event} fill={`url(#${id}-d)`} />
      <rect x="104" y="190" width="92" height="10" rx="5" fill="#ffffff" opacity="0.7" />
      {look.type === "Love" && <path d="M150 262 c -10 -14 -32 -6 -24 12 c 5 10 24 22 24 22 s 19 -12 24 -22 c 8 -18 -14 -26 -24 -12 z" fill="#ffffff" opacity="0.85" />}
      {look.type === "Fantasy" && <path d="M150 238 l 8 18 20 2 -15 13 5 20 -18 -11 -18 11 5 -20 -15 -13 20 -2 z" fill="#ffffff" opacity="0.85" />}
      {/* sparkles */}
      <path d="M58 70 l4 10 10 4 -10 4 -4 10 -4 -10 -10 -4 10 -4 z M244 214 l3 8 8 3 -8 3 -3 8 -3 -8 -8 -3 8 -3 z M80 166 l2 6 6 2 -6 2 -2 6 -2 -6 -6 -2 6 -2 z" fill="#ffffff" opacity="0.8" />
    </svg>
  );
}

function Cover({ look, big, eager }) {
  if (look.cover) {
    return <img className={styles.art} src={look.cover} alt={look.title} width="600" height="900" loading={eager ? "eager" : "lazy"} decoding="async" />;
  }
  return <LookArt look={look} big={big} />;
}

export function Palette({ pal, className }) {
  return (
    <span className={`${styles.palette} ${className || ""}`} aria-hidden="true">
      {pal.map((c) => <span key={c} style={{ background: c }} />)}
    </span>
  );
}

// One look. `badge`: "New" on this week's looks. `locked`: coming soon (no link, shows its date).
// `stars`: the visitor's best stars on it. `onPick`: play it in place (a button instead of a link).
export default function LookCard({ look, badge, big, locked, eager, stars, onPick }) {
  const body = (
    <>
      <span className={styles.cover}>
        <Cover look={look} big={big} eager={eager} />
        {badge && <span className={styles.newPill}>{badge}</span>}
        {/* three star slots once the visitor has played the look: their best stars, filled in gold */}
        {!locked && stars > 0 && (
          <span className={styles.starSlots} title={`Your best: ${stars} of 3 stars`} aria-label={`${stars} of 3 stars`}>
            {[1, 2, 3].map((n) => (
              <svg key={n} viewBox="0 0 24 24" width="14" height="14" aria-hidden="true" className={n <= stars ? styles.slotOn : styles.slotOff}>
                <path d="M12 2.8 l2.8 5.9 6.4 .8 -4.7 4.4 1.2 6.4 L12 17.2 6.3 20.3 l1.2 -6.4 -4.7 -4.4 6.4 -.8 z" />
              </svg>
            ))}
          </span>
        )}
        {locked && (
          <span className={styles.lock}>
            <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="5" y="11" width="14" height="10" rx="2.5" /><path d="M8 11 V8 a4 4 0 0 1 8 0 V11" />
            </svg>
            <span>{niceDate(look.date)}</span>
          </span>
        )}
      </span>
      <Palette pal={look.pal} />
      <span className={styles.cardText}>
        <span className={styles.cardTitle}>{shortTitle(look.title)}</span>
        <span className={styles.cardMeta}>{locked ? "Coming soon" : look.event}</span>
      </span>
    </>
  );
  const cls = `${styles.card} ${big ? styles.cardBig : ""} ${locked ? styles.cardLocked : ""}`;
  if (locked) return <div className={cls}>{body}</div>;
  if (onPick) return <button type="button" className={`${cls} ${styles.cardBtn}`} onClick={onPick}>{body}</button>;
  return <Link href={`/glamzy/${look.slug}`} prefetch={false} className={cls}>{body}</Link>;
}
